"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGlobalAuth = exports.SMARTBOOK_USER_TYPE = exports.AccountType = void 0;
require("whatwg-fetch");
const client_1 = require("@apollo/client");
const react_1 = require("react");
const react_cookie_1 = require("react-cookie");
const _1 = require(".");
const sessionUpdater_1 = require("./sessionUpdater");
const mutations_1 = require("./GraphQL/mutations");
const queries_1 = require("./GraphQL/queries");
const constants_1 = require("./constants");
var AccountType;
(function (AccountType) {
    AccountType["STANDARD"] = "STANDARD";
    AccountType["BUSINESS"] = "BUSINESS";
    AccountType["HOST"] = "HOST";
})(AccountType = exports.AccountType || (exports.AccountType = {}));
var SMARTBOOK_USER_TYPE;
(function (SMARTBOOK_USER_TYPE) {
    SMARTBOOK_USER_TYPE["BOOKER"] = "BOOKER";
    SMARTBOOK_USER_TYPE["HOST"] = "HOST";
    SMARTBOOK_USER_TYPE["PROCUREMENT"] = "PROCUREMENT";
})(SMARTBOOK_USER_TYPE = exports.SMARTBOOK_USER_TYPE || (exports.SMARTBOOK_USER_TYPE = {}));
exports.useGlobalAuth = () => {
    if (_1.globalAuthContext.state === null) {
        throw new Error('You must set globalAuthContext before calling useGlobalAuth');
    }
    const { client, refreshTokenCookie, sessionTokenCookie, rootService, selfServeService, user: cachedUser } = _1.globalAuthContext.state;
    const [cookies, setCookie, removeCookie] = react_cookie_1.useCookies([refreshTokenCookie, sessionTokenCookie]);
    const [user, setUser] = react_1.useState(cachedUser);
    const [authenticated, setAuthenticated] = react_1.useState(Boolean(cookies[sessionTokenCookie]) && Boolean(cookies[refreshTokenCookie]) && Boolean(user));
    const [processing, setProccessing] = react_1.useState(false);
    const handleSession = sessionUpdater_1.sessionUpdater({
        setCookie,
        setAuthenticated,
        setUser
    });
    const refreshTokenBoolean = Boolean(cookies[refreshTokenCookie]);
    const { data: sessionData, loading: sessionLoading, client: queryClient } = client_1.useQuery(queries_1.GET_SESSION_QUERY, Object.assign(Object.assign({}, (client && { client })), { skip: !refreshTokenBoolean || Boolean(user), variables: { token: cookies[refreshTokenCookie] } }));
    const [authenticateGuest, { loading: authGuestLoading }] = client_1.useMutation(mutations_1.AUTHENTICATE_USER_MUTATION, Object.assign({}, (client && { client })));
    const [createStandardGuestAccount, { loading: createStandardGuestAccountLoading }] = client_1.useMutation(mutations_1.CREATE_STANDARD_GUEST_ACCOUNT_MUTATION, Object.assign({}, (client && { client })));
    const [createBusinessGuestAccount, { loading: createBusinessGuestAccountLoading }] = client_1.useMutation(mutations_1.CREATE_BUSINESS_GUEST_ACCOUNT_MUTATION, Object.assign({}, (client && { client })));
    const [createHostAccount, { loading: createHostAccountLoading }] = client_1.useMutation(mutations_1.CREATE_HOST_ACCOUNT_MUTATION, Object.assign({}, (client && { client })));
    react_1.useEffect(() => {
        const session = sessionData === null || sessionData === void 0 ? void 0 : sessionData.getSession;
        if (session) {
            // eslint-disable-next-line no-void
            void (async () => {
                var _a, _b, _c;
                // Add smart book user context to session
                // This will likely be moved to auth system at a later date
                setProccessing(true);
                const res = await fetch(`https://lambda.headbox.com/smart-book-user-auth?email=${encodeURIComponent(session.user.email)}`);
                const { role } = await res.json();
                const resSelfServe = ((_a = _1.globalAuthContext.state) === null || _a === void 0 ? void 0 : _a.selfServeService) &&
                    (await fetch(selfServeService, {
                        method: 'POST',
                        body: JSON.stringify({
                            query: `
              query Host {
                getHostInfo(hostID:${session.user.id}){
                  isSelfServeHost
                }
              }`
                        }),
                        headers: {
                            'Content-Type': 'application/json',
                            'x-headbox-accesstoken': session.jwt
                        }
                    }));
                const isSmartBookHost = (_c = (_b = (await (resSelfServe === null || resSelfServe === void 0 ? void 0 : resSelfServe.json()))) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.getHostInfo.isSelfServeHost;
                const smartBook = isSmartBookHost ? SMARTBOOK_USER_TYPE.HOST : SMARTBOOK_USER_TYPE[role];
                setProccessing(false);
                if (smartBook !== undefined) {
                    return handleSession(Object.assign(Object.assign({}, session), { user: Object.assign(Object.assign({}, session.user), { smartBook }) }));
                }
                return handleSession(session);
            })();
        }
    }, [sessionData]);
    const login = async (input) => {
        const { data: authenticateGuestData } = await authenticateGuest({
            variables: { input }
        });
        handleSession(authenticateGuestData === null || authenticateGuestData === void 0 ? void 0 : authenticateGuestData.authenticateGuest);
    };
    const logout = () => {
        setProccessing(true);
        removeCookie(sessionTokenCookie, constants_1.COOKIE_OPTIONS);
        removeCookie(refreshTokenCookie, constants_1.COOKIE_OPTIONS);
        setAuthenticated(false);
        setUser(null);
        const logoutForm = document.createElement('form');
        logoutForm.action = `${rootService}/users/sign_out`;
        logoutForm.method = 'POST';
        const method = document.createElement('input');
        method.name = '_method';
        method.value = 'delete';
        method.type = 'hidden';
        logoutForm.appendChild(method);
        document.body.appendChild(logoutForm);
        logoutForm.submit();
    };
    const register = async (accountType, userDetails, password) => {
        switch (accountType) {
            case AccountType.BUSINESS: {
                const { data: createBusinessGuestAccounData } = await createBusinessGuestAccount({
                    variables: { input: { userDetails, password } }
                });
                handleSession(createBusinessGuestAccounData === null || createBusinessGuestAccounData === void 0 ? void 0 : createBusinessGuestAccounData.createBusinessGuestAccount);
                break;
            }
            case AccountType.HOST: {
                const { data: createHostAccounData } = await createHostAccount({
                    variables: { input: { userDetails, password } }
                });
                handleSession(createHostAccounData === null || createHostAccounData === void 0 ? void 0 : createHostAccounData.createHostAccount);
                break;
            }
            case AccountType.STANDARD: {
                const { data: createStandardGuestAccountData } = await createStandardGuestAccount({
                    variables: { input: { userDetails } }
                });
                handleSession(createStandardGuestAccountData === null || createStandardGuestAccountData === void 0 ? void 0 : createStandardGuestAccountData.createStandardGuestAccount);
                break;
            }
        }
    };
    const triageUser = async (email, country) => {
        const { data: guestTypeData } = await queryClient.query({
            query: queries_1.GUEST_TYPE_QUERY,
            variables: { email, country }
        });
        return guestTypeData === null || guestTypeData === void 0 ? void 0 : guestTypeData.getGuestType;
    };
    return {
        login,
        logout,
        register,
        triageUser,
        loading: processing ||
            sessionLoading ||
            authGuestLoading ||
            createStandardGuestAccountLoading ||
            createBusinessGuestAccountLoading ||
            createHostAccountLoading,
        authenticated,
        user,
        jwt: cookies[sessionTokenCookie]
    };
};
